import { createSelector } from 'reselect';
import { userRoleSelector, userPersonalInformationSelector, userEffectiveAgencyIdSelector } from 'src/store/user/user.selectors';
import { selectedAgencyTypeSelector, userAgenciesSelector } from 'src/store/user-info/user-info.selectors';
import { featureFlagValuesSelector, featureFlagsReadySelector } from 'src/store/feature-flags/feature-flags.selectors';
import { NAV_REGISTRY, NOT_FOUND_ROUTE } from 'src/config/navigation.registry';
import {
    buildNavigationContext,
    getAccessibleRoutes,
    getDefaultRouteForContext,
    getSidebarNavBySection,
    toRouteDefinition,
} from 'src/config/navigation.utils';

import { normalizeRoleName } from 'src/constants/roles.constants';

export const navigationContextSelector = createSelector(
    [userRoleSelector, selectedAgencyTypeSelector, featureFlagValuesSelector],
    (userRole, agencyType, featureFlags) =>
        buildNavigationContext({ userRole, agencyType, featureFlags }),
);

/**
 * True when user role and agency context are loaded enough to render
 * role- and agency-type-filtered routes and sidebar navigation.
 */
export const isAppNavigationReadySelector = createSelector(
    [
        userPersonalInformationSelector,
        userRoleSelector,
        userAgenciesSelector,
        userEffectiveAgencyIdSelector,
        featureFlagsReadySelector,
    ],
    (personalInformation, userRole, agencies, agencyId, featureFlagsReady) => {
        if (!featureFlagsReady) {
            return false;
        }

        if (personalInformation?.loading) {
            return false;
        }

        if (!userRole) {
            return false;
        }

        if (agencies?.loading) {
            return false;
        }

        // When agencies exist, wait until the bootstrap thunk sets agencyId
        // so agency-type filtering is accurate on first paint.
        if (agencies?.list?.length > 0 && !agencyId) {
            return false;
        }

        return true;
    },
);

export const accessiblePrivateRoutesSelector = createSelector(
    [navigationContextSelector, isAppNavigationReadySelector],
    (context, isReady) => {
        if (!isReady) {
            return [];
        }

        return [
            ...getAccessibleRoutes(NAV_REGISTRY, context),
            toRouteDefinition({ ...NOT_FOUND_ROUTE, allowedRoles: undefined }),
        ];
    },
);

export const sidebarNavSelector = createSelector(
    [navigationContextSelector, isAppNavigationReadySelector],
    (context, isReady) => {
        if (!isReady) {
            return null;
        }
        return getSidebarNavBySection(NAV_REGISTRY, context);
    },
);

export const defaultRouteForContextSelector = createSelector(
    [navigationContextSelector, isAppNavigationReadySelector],
    (context, isReady) => {
        if (!isReady) {
            return null;
        }
        return getDefaultRouteForContext(NAV_REGISTRY, context);
    },
);

/**
 * Filter routes by role only (legacy API — does not apply agency-type filtering).
 * Prefer `accessiblePrivateRoutesSelector` for the authenticated app shell.
 */
export const filterRoutesByRole = (routes, userRole) => {
    const normalized = normalizeRoleName(userRole);
    return routes.filter((route) =>
        !route.allowedRoles || route.allowedRoles.includes(normalized)
    );
};
